import React, { useEffect, useState } from "react";
import "./WallsTab.css";
import WallSelector, { UiWall } from "./components/WallSelector";
import { useConfiguration } from "src/contexts/ConfigurationContext";
import { Wall } from "src/types/configuration";
import SelectUnselectExtended from "src/components/Input/SelectUnselectExtended/SelectUnselectExtended";
import WallDoorWindow from "src/components/WallDoorWindow/WallDoorWindow";
// @ts-ignore
import isEqual from "lodash/isEqual";
import Text from "src/components/Text/Text";
import HorizontalTabs from "src/components/HorizontalTabs/HorizontalTabs";
import ColorSelect from "src/components/Input/Select/ColorSelect";
import { isFlatRoofType } from "src/helpers/roofType";

interface WallComponentProps {
  wall: Wall;
  onWallChange: (wall: Wall) => void;
  handleWoodWallChange?: (woodWallEnable: boolean) => void;
  onDoneClick?: () => void;
}

const WoodWallComponent: React.FC<WallComponentProps> = ({
  wall,
  onWallChange,
}) => {
  const {
    state,
    activeWoodTab,
    setActiveWoodTab,
    activeStorageColorTab,
    setActiveStorageColorTab,
  } = useConfiguration();
  const [showDoorWindowComponent, setShowDoorWindowComponent] = useState(false);

  const handleOuterFinishChange = (outerColor: string) => {
    if (wall.woodWall.outerColor === outerColor) return;

    const woodWall = { ...wall.woodWall, outerColor };
    if (outerColor.startsWith("wall_blockhut")) {
      woodWall.innerColor = "empty";
    }

    const updatedWall = { ...wall, woodWall };
    if (!isEqual(wall, updatedWall)) onWallChange(updatedWall);
  };

  const handleWallChange = (updatedWall: Wall) => {
    if (!isEqual(wall, updatedWall)) onWallChange(updatedWall);
  };

  const handleInnerFinishChange = (innerColor: string) => {
    if (wall.woodWall.outerColor.startsWith("wall_blockhut")) return;
    if (wall.woodWall.innerColor === innerColor) return;

    const woodWall = { ...wall.woodWall, innerColor };
    const updatedWall = { ...wall, woodWall };
    if (!isEqual(wall, updatedWall)) onWallChange(updatedWall);
  };

  const colorTabs = [
    {
      id: 1,
      title: "Buitenzijde",
      component: (
        <ColorSelect
          value={wall.woodWall.outerColor}
          onChange={handleOuterFinishChange}
        />
      ),
    },
    {
      id: 2,
      title: "Binnenzijde",
      disabled:
        wall.woodWall.outerColor === "empty" ||
        wall.woodWall.outerColor.includes("blockhut"),
      component: (
        <ColorSelect
          value={wall.woodWall.innerColor}
          onChange={handleInnerFinishChange}
          innerColor
          disabled={
            wall.woodWall.outerColor === "empty" ||
            wall.woodWall.outerColor.includes("blockhut")
          }
        />
      ),
    },
  ];

  const colorSection = (
    <div className="wood-wall-row">
      <HorizontalTabs
        className="storage-color-inner-tabs"
        tabs={colorTabs}
        value={activeStorageColorTab}
        onChange={setActiveStorageColorTab}
      />
    </div>
  );

  const canShowDoorWindow =
    isFlatRoofType(state.roofType) ||
    (state.roofType === "kapschuur" && !wall?.id.startsWith("h-1"));

  if (!canShowDoorWindow) {
    return colorSection;
  }

  const woodWallTabs = [
    {
      id: 1,
      title: "Wanden",
      component: colorSection,
    },
    {
      id: 2,
      title: "Deur & raam",
      component: (
        <WallDoorWindow
          key={`wall-${wall.id}-${wall.woodWall.door}`}
          onWallChange={handleWallChange}
          wall={wall}
          disabled={wall.woodWall.outerColor === "empty"}
          showComponent={showDoorWindowComponent}
          setShowComponent={setShowDoorWindowComponent}
        />
      ),
    },
  ];

  const currentTab =
    woodWallTabs.find((tab) => tab.id === activeWoodTab)?.id ??
    woodWallTabs[0].id;

  return (
    <HorizontalTabs
      className="walls-wood-tabs"
      tabs={woodWallTabs}
      value={currentTab}
      onChange={setActiveWoodTab}
    />
  );
};

const GlassWallDoneButton: React.FC<{
  label: string;
  onClick: () => void;
}> = ({ label, onClick }) => {
  return (
    <button
      type="button"
      className="storage-complete-button"
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
    >
      {label}
    </button>
  );
};

const SlidingGlassWallComponent: React.FC<WallComponentProps> = ({
  wall,
  onWallChange,
  onDoneClick,
}) => {
  const handleSideChange = (side: string) => {
    if (wall.slidingGlassWallOptions.schuifrichting === side) return;
    const updateWall = {
      ...wall,
      slidingGlassWallOptions: {
        ...wall.slidingGlassWallOptions,
        schuifrichting: side,
      },
    };
    onWallChange(updateWall);
  };

  const handleSideEnableChange = (sideEnable: boolean) => {
    if (
      (wall.slidingGlassWallOptions.schuifrichting !== "empty") ===
      sideEnable
    )
      return;
    const slidingGlassWallOptions = { ...wall.slidingGlassWallOptions };

    if (sideEnable) {
      slidingGlassWallOptions.schuifrichting =
        slidingGlassWallOptions.schuifrichting === "empty"
          ? "left"
          : slidingGlassWallOptions.schuifrichting;
    } else {
      slidingGlassWallOptions.schuifrichting = "empty";
    }

    const updateWall = {
      ...wall,
      slidingGlassWallOptions: slidingGlassWallOptions,
    };
    if (!isEqual(wall, updateWall)) onWallChange(updateWall);
  };

  const handleDoorBrushesChange = (doorBrushesEnable: boolean) => {
    if ((wall.slidingGlassWallOptions.doorBrushes === 1) === doorBrushesEnable)
      return;
    const slidingGlassWallOptions = { ...wall.slidingGlassWallOptions };
    slidingGlassWallOptions.doorBrushes = doorBrushesEnable ? 1 : 0;
    const updateWall = {
      ...wall,
      slidingGlassWallOptions: slidingGlassWallOptions,
    };
    if (!isEqual(wall, updateWall)) onWallChange(updateWall);
  };

  const handleStainlessSteelHandleChange = (
    stainlessSteelHandleEnable: boolean
  ) => {
    if (
      (wall.slidingGlassWallOptions.stainlessSteelHandle === 1) ===
      stainlessSteelHandleEnable
    )
      return;
    const slidingGlassWallOptions = { ...wall.slidingGlassWallOptions };
    slidingGlassWallOptions.stainlessSteelHandle = stainlessSteelHandleEnable
      ? 1
      : 0;
    const updateWall = {
      ...wall,
      slidingGlassWallOptions: slidingGlassWallOptions,
    };
    if (!isEqual(wall, updateWall)) onWallChange(updateWall);
  };

  const handleLockProfileChange = (lockProfileEnable: boolean) => {
    if ((wall.slidingGlassWallOptions.lockProfile === 1) === lockProfileEnable)
      return;
    const slidingGlassWallOptions = { ...wall.slidingGlassWallOptions };
    slidingGlassWallOptions.lockProfile = lockProfileEnable ? 1 : 0;
    const updateWall = {
      ...wall,
      slidingGlassWallOptions: slidingGlassWallOptions,
    };
    if (!isEqual(wall, updateWall)) onWallChange(updateWall);
  };

  const handleContributorsChange = (contributorsEnable: boolean) => {
    if (
      (wall.slidingGlassWallOptions.contributors === 1) ===
      contributorsEnable
    )
      return;
    const slidingGlassWallOptions = { ...wall.slidingGlassWallOptions };
    slidingGlassWallOptions.contributors = contributorsEnable ? 1 : 0;
    const updateWall = {
      ...wall,
      slidingGlassWallOptions: slidingGlassWallOptions,
    };
    if (!isEqual(wall, updateWall)) onWallChange(updateWall);
  };

  const handleGlassColorChange = (color: string) => {
    if (wall.slidingGlassWallOptions.glassColor === color) return;
    const updateWall = {
      ...wall,
      slidingGlassWallOptions: {
        ...wall.slidingGlassWallOptions,
        glassColor: color,
      },
    };
    onWallChange(updateWall);
  };

  const handleGlassColorEnableChange = (colorEnable: boolean) => {
    if ((wall.slidingGlassWallOptions.glassColor !== "empty") === colorEnable)
      return;
    const slidingGlassWallOptions = { ...wall.slidingGlassWallOptions };

    if (colorEnable) {
      slidingGlassWallOptions.glassColor =
        slidingGlassWallOptions.glassColor === "empty"
          ? "zwart"
          : slidingGlassWallOptions.glassColor;
    } else {
      slidingGlassWallOptions.glassColor = "empty";
    }

    const updateWall = {
      ...wall,
      slidingGlassWallOptions: slidingGlassWallOptions,
    };
    if (!isEqual(wall, updateWall)) onWallChange(updateWall);
  };

  const SideComponent: React.FC = () => (
    <HorizontalTabs
      tabs={[
        { id: 1, title: "Links", component: null },
        { id: 2, title: "Rechts", component: null },
      ]}
      value={wall.slidingGlassWallOptions.schuifrichting === "left" ? 1 : 2}
      onChange={(id: number) => handleSideChange(id === 1 ? "left" : "right")}
    />
  );

  const GlassColorComponent: React.FC = () => (
    <HorizontalTabs
      tabs={[
        { id: 1, title: "Zwart", component: null },
        { id: 2, title: "Antraciet", component: null },
        { id: 3, title: "Wit", component: null },
      ]}
      value={
        wall.slidingGlassWallOptions.glassColor === "zwart"
          ? 1
          : wall.slidingGlassWallOptions.glassColor === "antraciet"
          ? 2
          : 3
      }
      onChange={(id: number) =>
        handleGlassColorChange(
          id === 1 ? "zwart" : id === 2 ? "antraciet" : "wit"
        )
      }
    />
  );

  const slidingGlassWallOptions = [
    // {
    //   name: "Tochtborstels",
    //   imageUrl: `${process.env.PUBLIC_URL}/images/space-option-thumb-1.png`,
    //   description:
    //     "Tochtborstels geven extra bescherming tegen weer, wind en stof, en kunnen wij u van harte aanbevelen.",
    //   getIsSelected: () => wall.slidingGlassWallOptions.doorBrushes === 1,
    //   onSelect: () => handleDoorBrushesChange(true),
    //   onUnselect: () => handleDoorBrushesChange(false),
    // },
    // {
    //   name: "Handgreep RVS",
    //   imageUrl: `${process.env.PUBLIC_URL}/images/space-option-thumb-2.png`,
    //   description: "",
    //   getIsSelected: () =>
    //     wall.slidingGlassWallOptions.stainlessSteelHandle === 1,
    //   onSelect: () => handleStainlessSteelHandleChange(true),
    //   onUnselect: () => handleStainlessSteelHandleChange(false),
    // },
    // {
    //   name: "Vergrendelprofiel",
    //   imageUrl: `${process.env.PUBLIC_URL}/images/space-option-thumb-3.png`,
    //   description:
    //     "De vergrendelprofielen zitten altijd aan beide zijden, zodat beide zijden vergrendeld kunnen worden.",
    //   getIsSelected: () => wall.slidingGlassWallOptions.lockProfile === 1,
    //   onSelect: () => handleLockProfileChange(true),
    //   onUnselect: () => handleLockProfileChange(false),
    // },
    {
      name: "Schuifrichting",
      imageUrl: `${process.env.PUBLIC_URL}/images/space-option-thumb-4.png`,
      description:
        "Geef hier de gewenste schuifrichting op van uw glazen schuifwand. Geef dit op vanaf de positie als u er vanaf de buitenzijde recht voorstaat.",
      Component: SideComponent,
      getIsSelected: () =>
        wall.slidingGlassWallOptions.schuifrichting !== "empty",
      onSelect: () => handleSideEnableChange(true),
      onUnselect: () => handleSideEnableChange(false),
    },
    {
      name: "Kleur profielen",
      imageUrl: `${process.env.PUBLIC_URL}/images/space-option-thumb-1.png`,
      description:
        "Selecteer de gewenste kleur voor uw glazen schuifwand profielen.",
      Component: GlassColorComponent,
      getIsSelected: () => wall.slidingGlassWallOptions.glassColor !== "empty",
      onSelect: () => handleGlassColorEnableChange(true),
      onUnselect: () => handleGlassColorEnableChange(false),
    },
    // {
    //   name: "Meenemers (GRATIS)",
    //   imageUrl: `${process.env.PUBLIC_URL}/images/space-option-thumb-5.png`,
    //   description:
    //     "Meenemers zorgen ervoor dat wanneer u het eerste paneel open of dicht schuift dat de andere ruitpanelen dan automatisch meeschuiven.",
    //   getIsSelected: () => wall.slidingGlassWallOptions.contributors === 1,
    //   onSelect: () => handleContributorsChange(true),
    //   onUnselect: () => handleContributorsChange(false),
    // },
  ];

  return (
    <div className="sliding-glass-wall-options">
      <Text
        content={"Opties Glazen schuifwand"}
        className={"sliding-glass-wall-options-title"}
      />
      <Text
        content={
          "De glazen schuifwanden worden compleet geleverd, incl. glaswanden, profielen, meenemers, deurgreep en tochtprofielen."
        }
        className={"sliding-glass-wall-options-subtitle"}
      />
      {slidingGlassWallOptions.map((option) => (
        <SelectUnselectExtended
          key={option.name}
          name={option.name}
          imageUrl={option.imageUrl}
          description={option.description}
          Component={option.Component}
          isSelected={option.getIsSelected()}
          onSelect={option.onSelect}
          onUnselect={option.onUnselect}
          stopPropagation={true}
          scrollOnToggle={false}
        />
      ))}
      {onDoneClick && (
        <GlassWallDoneButton
          label="Glazen schuifwand gereed"
          onClick={onDoneClick}
        />
      )}
    </div>
  );
};

const WallsTab: React.FC = () => {
  const {
    state,
    addWall,
    setWalls,
    configuration,
    configurator,
    setActiveWoodTab,

    setActiveStorageColorTab,
  } = useConfiguration();
  const [selectedWall, setSelectedWall] = useState<UiWall | null>(null);
  const [activeWalls, setActiveWalls] = useState<string[]>([]);
  const [storageWalls, setStorageWalls] = useState<string[]>([]);
  const [wall, setWall] = useState<Wall>(
    configuration.findWallbyUiWall(selectedWall)
  );
  const [showGlassWallDoneButton, setShowGlassWallDoneButton] = useState(false);

  useEffect(() => {
    setShowGlassWallDoneButton(false);
  }, [wall?.id, selectedWall?.id]);

  const handleSlidingGlassWallChange = (slidingGlassWallEnable: boolean) => {
    if (!wall) return;

    let slidingGlassWall = "empty";
    const slidingGlassWallOptions = { ...wall.slidingGlassWallOptions };

    if (slidingGlassWallEnable) {
      slidingGlassWall = configurator.getSlidingGlassDoor(wall);
      slidingGlassWallOptions.glasbok = 1;

      // scroll to top
      // setTimeout(() => {
      //   const tabElement = document.querySelector(
      //     `.walls-tab .wall-glass-wall`
      //   );
      //   if (tabElement) scrollTo(tabElement);
      // }, 80);
    }

    const updatedWall: Wall = {
      id: wall.id,
      position: wall.position,
      isRightSide: wall.isRightSide,
      storage: wall.storage,
      inner: wall.inner,
      slidingGlassWall,
      slidingGlassWallOptions,
      woodWall: configuration.getDefaultWoodWall(),
      elementWall: configuration.getDefaultElementWall(),
    };

    addWall(updatedWall);
    setWall(updatedWall);
  };

  const handleWoodWallChange = (woodWallEnable: boolean) => {
    if (!wall) return;

    if (woodWallEnable) {
      setShowGlassWallDoneButton(false);
      const woodWall = { ...wall.woodWall };
      woodWall.outerColor = "wall_vuren_black";
      const updatedWall = {
        ...wall,
        woodWall: woodWall,
        slidingGlassWall: "empty",
      };
      addWall(updatedWall);
      setWall(updatedWall);

      // scroll to top
      // setTimeout(() => {
      //   const tabElement = document.querySelector(`.walls-tab .wall-wood-wall`);
      //   if (tabElement) scrollTo(tabElement);
      // }, 80);
    } else {
      const woodWall = configuration.getDefaultWoodWall();
      const updatedWall = {
        ...wall,
        woodWall,
        slidingGlassWall: "empty",
      };
      addWall(updatedWall);
      setWall(updatedWall);
    }
  };

  const handleSelectedWall = (newUiWall: UiWall | null) => {
    if (!wall) return;
    const newWall = configuration.findWallbyUiWall(newUiWall);

    if (newWall.storage !== "empty") {
      return;
    }

    const columns = configurator.getColumns();

    if (newUiWall?.id === `v-0-${columns}`) {
      newWall.isRightSide = true;
    }

    setSelectedWall(newUiWall);
    setWall(newWall);

    configurator.focusOnWall(newWall);
    configurator.selectWall(newWall);

    // scroll to top
    // setTimeout(() => {
    //   const tabElement = document.querySelector(`.walls-tab .wall-wood-wall`);
    //   if (tabElement) scrollTo(tabElement);
    // }, 80);
  };

  const handleWallChange = (updatedWall: Wall) => {
    if (!wall) return;

    if (!isEqual(wall, updatedWall)) {
      addWall(updatedWall);
      setWall(updatedWall);
    }
  };

  const handleWallDelete = (newUiWall: UiWall | null) => {
    setSelectedWall(null);
    configurator.deselectWalls();
    if (!wall) return;
    if (wall.storage === "empty")
      setWalls(state.walls.filter((wall) => wall.id !== newUiWall?.id));
  };

  useEffect(() => {
    if (!wall) return;

    setActiveWalls(
      state.walls
        .filter((wall) => {
          if (wall.storage !== "empty") return false;
          if (wall.slidingGlassWall !== "empty") return true;
          return wall.woodWall.outerColor !== "empty";
        })
        .map((wall) => wall.id)
    );

    setStorageWalls(
      state.walls
        .filter((wall) => {
          return wall.storage !== "empty";
        })
        .map((wall) => wall.id)
    );
  }, [state.walls, wall]);

  return (
    <div className="walls-tab">
      <div className="walls-selector-container">
        <WallSelector
          onWallSelect={handleSelectedWall}
          onWallDeselect={handleWallDelete}
          activeWalls={activeWalls}
          storageWalls={storageWalls}
        />
      </div>
      {selectedWall && isFlatRoofType(state.roofType) && (
        <div className="wall-glass-wall">
          <SelectUnselectExtended
            name="Glazen schuifwand"
            imageUrl={`${process.env.PUBLIC_URL}/images/glass-door.png`}
            description=""
            Component={
              configurator.getSlidingGlassDoor(wall) !== "empty"
                ? () => (
                    <SlidingGlassWallComponent
                      wall={wall}
                      onWallChange={handleWallChange}
                      onDoneClick={() =>
                        setShowGlassWallDoneButton(!showGlassWallDoneButton)
                      }
                    />
                  )
                : undefined
            }
            isSelected={wall.slidingGlassWall !== "empty"}
            onSelect={() => handleSlidingGlassWallChange(true)}
            onUnselect={() => handleSlidingGlassWallChange(false)}
            showComponent={showGlassWallDoneButton}
            onShowComponentToggle={() => {
              return setShowGlassWallDoneButton(!showGlassWallDoneButton);
            }}
            disabled={configurator.getSlidingGlassDoor(wall) === "empty"}
          />
        </div>
      )}
      {selectedWall && (
        <div className="wall-wood-wall">
          <SelectUnselectExtended
            name="Houten wand"
            imageUrl={`${process.env.PUBLIC_URL}/images/buitenwand-3.png`}
            description=""
            Component={() => (
              <WoodWallComponent wall={wall} onWallChange={handleWallChange} />
            )}
            isSelected={wall.woodWall.outerColor !== "empty"}
            onSelect={() => {
              handleWoodWallChange(true);
              setActiveWoodTab(1);
              setActiveStorageColorTab(1);
            }}
            onUnselect={() => handleWoodWallChange(false)}
          />
        </div>
      )}
    </div>
  );
};

export default WallsTab;
